import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Ablageort, Angebot, Ansprechpartner, ApiClient, Auftrag, Benutzer, Unternehmen,
              VorgangsBezug } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Eingabe, Formular, Hinweis } from "@kundeninfo/ui/me-design";
import { Vorgangsabschnitt } from "../Vorgangsabschnitt.js";
import { useSectionFolds } from "../vorgangsabschnitt/sectionFolds.js";
import { SECTION_TEXT } from "../vorgangsabschnitt/texts.js";
import { Dateiwahl, KiKnopf } from "../meHuellen.js";
import { apiMeldung, betragText, betragZahl } from "../vorgangsdetail.js";
import { isTooLarge, sendInPackets, tooLargeResult } from "../documentFiles/upload.js";
import type { UploadRow } from "../documentFiles/upload.js";
import { StorageLine, UploadResultList } from "../documentFiles/UploadResultList.js";
import { DOCUMENT_FILE_TEXT } from "../documentFiles/texts.js";
import { ANGEBOT_STATUS_LABEL, AUFTRAG_STATUS_LABEL } from "../../vorgangsstatus.js";
import { HISTORICAL_TEXT as T, STORAGE_TEMPLATE } from "./texts.js";
import type { HistoricalKind } from "./texts.js";
import "./historical.css";

/*
 * Historische Angebote und Aufträge erfassen und bearbeiten (Spec „Historische Vorgänge anlegen und anzeigen“, Aufgabe
 * 26): Altbestand ohne Positionen und ohne Beleg — Kopfdaten mit der Nummer aus dem Vorsystem, dazu Dateien.
 */

/** Die Wege je Art: Anlegen, Ändern, das Neuladen im fremden Stand (409), am Auftrag die Angebote (Herkunft). */
type KindWays = {
  offer: "createHistoricalOffer" | "updateHistoricalOffer" | "getOffer";
  order: "createHistoricalOrder" | "updateHistoricalOrder" | "getSalesOrder" | "listOffers";
};
/** Die Wege des Panels: die der Art, dazu Hochladen, Verknüpfen und der Ablageort beim Bearbeiten. */
export type HistoricalRecordClient<A extends HistoricalKind> = Pick<ApiClient,
  KindWays[A] | "uploadDocumentFiles" | "linkDocumentFile" | "listDocumentFiles">;
/** Art und Klient gehören zusammen: Das Angebotspanel braucht keine Auftragswege und umgekehrt. */
export type HistoricalKindClient<Extra = unknown> =
  | { art: "offer"; client: HistoricalRecordClient<"offer"> & Extra }
  | { art: "order"; client: HistoricalRecordClient<"order"> & Extra };

/** Die Auswahllisten: Ansprechpartner und Benutzer, am Auftrag die historischen Angebote des Kunden (Herkunftsangebot). */
export type HistoricalChoices = {
  kontakte: readonly Ansprechpartner[];
  benutzer: readonly Benutzer[];
  historischeAngebote: readonly Angebot[];
};

export type HistoricalRecordPanelProps = HistoricalKindClient & {
  kundeId: string;
  /** Der bestehende Vorgang (Bearbeiten), `null` beim Erfassen. */
  vorhanden: Angebot | Auftrag | null;
  /** Die wählbaren Unternehmen (Kundenbeziehung); beim Bearbeiten nur zur Anzeige. */
  firmen: readonly Unternehmen[];
  umfeld: HistoricalChoices;
  /** Der Vorgang steht (und seine Dateien so weit wie möglich): Der Rahmen lädt nach und schließt. */
  onGespeichert: (id: string) => void;
  onAbbrechen: () => void;
};

/** Die Status, die ein historischer Vorgang haben kann (Entwurf und Veraltet gibt es für Altbestand nicht). */
const STATUS_OPTIONS: Readonly<Record<HistoricalKind, readonly { value: string; text: string }[]>> = {
  offer: (["SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"] as const).map((s) => ({ value: s, text: ANGEBOT_STATUS_LABEL[s] })),
  order: (["ACTIVE", "COMPLETED", "CANCELLED"] as const).map((s) => ({ value: s, text: AUFTRAG_STATUS_LABEL[s] })),
};

/** Das Datum heißt am Server je Art anders. */
const DATE_FIELD: Readonly<Record<HistoricalKind, "createdOn" | "orderedOn">> = { offer: "createdOn", order: "orderedOn" };

type Draft = {
  companyId: string; title: string; legacyNumber: string; date: string; netAmount: string; status: string;
  contactId: string; responsibleUserId: string; note: string; offerId: string;
};

const isOffer = (v: Angebot | Auftrag): v is Angebot => "createdOn" in v;

function draftFrom(v: Angebot | Auftrag | null, companies: readonly Unternehmen[]): Draft {
  if (v === null) {
    return { companyId: companies.length === 1 ? (companies[0]?.id ?? "") : "", title: "", legacyNumber: "", date: "",
      netAmount: "", status: "", contactId: "", responsibleUserId: "", note: "", offerId: "" };
  }
  return {
    companyId: v.companyId, title: v.title, legacyNumber: v.legacyNumber ?? "",
    date: isOffer(v) ? v.createdOn : v.orderedOn, netAmount: betragText(v.netAmount), status: v.status,
    contactId: v.contactId ?? "", responsibleUserId: v.responsibleUserId ?? "",
    note: isOffer(v) ? (v.note ?? "") : "", offerId: isOffer(v) ? "" : (v.offerId ?? ""),
  };
}

const sameDraft = (a: Draft, b: Draft): boolean =>
  (Object.keys(a) as (keyof Draft)[]).every((k) => a[k] === b[k]);

/** Die Ablagezeile vor dem Anlegen: `‹Vorlage› › ‹Jahr aus Datum› › ‹alte Nummer›` — ohne Kürzelzusatz, den setzt erst
 *  der Upload (`storageInfo`, Aufgabe 14). `null`, solange Datum oder alte Nummer fehlen. */
export function storagePreview(kind: HistoricalKind, date: string, legacyNumber: string): string[] | null {
  const year = /^(\d{4})-\d{2}-\d{2}$/.exec(date)?.[1];
  const number = legacyNumber.trim();
  if (year === undefined || number === "") return null;
  return [STORAGE_TEMPLATE[kind === "offer" ? "OFFER" : "SALES_ORDER"], year, number];
}

/** Nochmals senden lohnt nur, was abgewiesen wurde und nicht zu groß ist. */
const retriable = (r: UploadRow): boolean =>
  r.result !== null && r.result.status === "rejected" && !isTooLarge(r.file);
/** Abgelegt (neu oder als vorhandene Datei verknüpft) — sonst steht die Datei noch offen da. */
const settled = (r: UploadRow): boolean => r.result?.status === "created" || r.result?.status === "linked";

/** Lädt die historischen Angebote des Kunden — die Auswahl „Herkunftsangebot“ am historischen Auftrag. */
export function useHistoricalOffers(client: Pick<ApiClient, "listOffers"> | null, kundeId: string, active: boolean):
    Angebot[] {
  const [offers, setOffers] = useState<Angebot[]>([]);
  useEffect(() => {
    if (!active || client === null) return undefined;
    let gone = false;
    client.listOffers(kundeId)
      .then(({ items }) => { if (!gone) setOffers(items.filter((a) => a.kind === "HISTORICAL")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { gone = true; };
  }, [client, kundeId, active]);
  return offers;
}

/**
 * Das Erfassen im BEREICH, sobald der Kunde gewählt ist: lädt die Auswahllisten dieses Kunden (aktive Ansprechpartner,
 * aktive Benutzer, am Auftrag die historischen Angebote) und zeichnet das Panel. Die Akte reicht ihre schon geladenen
 * Listen direkt an das Panel.
 */
export function HistoricalCreateForCustomer(p: HistoricalKindClient<Pick<ApiClient, "listContacts" | "listUsers">> & {
  kundeId: string; firmen: readonly Unternehmen[]; onGespeichert: (id: string) => void; onAbbrechen: () => void;
}): ReactElement {
  const { client, kundeId } = p;
  const [contacts, setContacts] = useState<Ansprechpartner[]>([]);
  const [users, setUsers] = useState<Benutzer[]>([]);
  const historicalOffers = useHistoricalOffers(p.art === "order" ? p.client : null, kundeId, true);
  useEffect(() => {
    let gone = false;
    client.listContacts(kundeId)
      .then(({ items }) => { if (!gone) setContacts(items.filter((k) => k.status === "ACTIVE")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    client.listUsers()
      .then(({ items }) => { if (!gone) setUsers(items.filter((b) => b.active)); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { gone = true; };
  }, [client, kundeId]);
  return (
    <HistoricalRecordPanel {...p} vorhanden={null}
      umfeld={{ kontakte: contacts, benutzer: users, historischeAngebote: historicalOffers }} />
  );
}

/**
 * Das Modalpanel „Historisches Angebot erfassen“ / „Historischen Auftrag erfassen“ (und Bearbeiten mit `vorhanden`):
 * rechts angeschlagen, mit Kreuz, Größe nach Inhalt. Zwei Abschnitte: „Kopfdaten“ und „Dateien“ (Mehrfachwahl,
 * Ablagezeile). „Erfassen“ ist die Bestätigung (216c): Es legt erst den Vorgang an und lädt danach die Dateien hoch. Geht
 * eine Datei nicht durch, bleibt der Vorgang stehen und das Panel offen: je Datei das Ergebnis (das Schlechteste oben),
 * „Erneut versuchen“ schickt nur die gescheiterten. Ab dem Speichern ist jeder Ausgang „Fertig“ (der Vorgang existiert).
 */
export function HistoricalRecordPanel(p: HistoricalRecordPanelProps): ReactElement {
  const { client, kundeId, art, firmen: companies, umfeld: choices } = p;
  /** Der Stand, gegen den gespeichert wird (nach „Neu laden“ der frische). */
  const [base, setBase] = useState(p.vorhanden);
  const [initial, setInitial] = useState(() => draftFrom(p.vorhanden, companies));
  const [draft, setDraft] = useState(initial);
  const [rows, setRows] = useState<UploadRow[]>([]);
  /** Die Kennung des gespeicherten Vorgangs — ab hier wird nur noch hochgeladen. */
  const [savedId, setSavedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /** Der Ablageort des bestehenden Vorgangs (beim Bearbeiten vom Server, mit „wird beim Hochladen angelegt“). */
  const [storage, setStorage] = useState<Ablageort | null>(null);
  const sections = useSectionFolds({ head: true, files: true });
  const refKind: VorgangsBezug["art"] = art === "offer" ? "offers" : "orders";
  const dateField = DATE_FIELD[art];

  const existingId = p.vorhanden?.id ?? null;
  useEffect(() => {
    if (existingId === null) return undefined;
    let gone = false;
    client.listDocumentFiles(kundeId, { art: refKind, id: existingId })
      .then((s) => { if (!gone) setStorage(s.storage); })
      .catch(() => { /* ohne Ablagezeile bleibt das Panel bedienbar */ });
    return () => { gone = true; };
  }, [client, kundeId, refKind, existingId]);

  const fields = ["companyId", "title", "legacyNumber", dateField, "netAmount", "status", "contactId",
    "responsibleUserId", ...(art === "offer" ? ["note"] : ["offerId"])];
  const form = useFormularZustand<Angebot | Auftrag>({
    speichern: async () => {
      try {
        return await save();
      } catch (e) {
        if (e instanceof ApiError && e.status === 422) sections.head.oeffnen();
        throw e;
      }
    },
    neuLaden: async () => {
      if (base === null) return;
      const fresh = p.art === "offer" ? await p.client.getOffer(kundeId, base.id)
        : await p.client.getSalesOrder(kundeId, base.id);
      setBase(fresh);
      const d = draftFrom(fresh, companies);
      setInitial(d);
      setDraft(d);
    },
    bekannteFelder: fields,
    beschriftungen: {
      companyId: T.company, title: T.title, legacyNumber: T.legacyNumber, [dateField]: T.date, netAmount: T.netAmount,
      status: T.status, contactId: T.contact, responsibleUserId: T.responsible, note: T.note, offerId: T.sourceOffer,
    },
  });

  /** Was die Maske selbst prüft, geht denselben Weg wie ein 422 des Servers: an das Feld. */
  function validateRequired(): ApiError | null {
    const found: { field: string; message: string }[] = [];
    if (base === null && draft.companyId === "") found.push({ field: "companyId", message: T.required });
    if (draft.status === "") found.push({ field: "status", message: T.required });
    if (Number.isNaN(betragZahl(draft.netAmount)) || draft.netAmount.trim() === "") {
      found.push({ field: "netAmount", message: T.amountInvalid });
    }
    return found.length === 0 ? null : new ApiError(422, "VALIDIERUNG", T.saveFailed, found);
  }

  async function save(): Promise<Angebot | Auftrag> {
    const invalid = validateRequired();
    if (invalid !== null) throw invalid;
    const common = {
      title: draft.title, legacyNumber: draft.legacyNumber, netAmount: betragZahl(draft.netAmount),
      contactId: draft.contactId === "" ? null : draft.contactId,
      responsibleUserId: draft.responsibleUserId === "" ? null : draft.responsibleUserId,
    };
    if (p.art === "offer") {
      const status = draft.status as "SENT" | "ACCEPTED" | "REJECTED" | "WITHDRAWN";
      const rest = { ...common, createdOn: draft.date, status, note: draft.note === "" ? null : draft.note };
      return base === null ? p.client.createHistoricalOffer(kundeId, { companyId: draft.companyId, ...rest })
        : p.client.updateHistoricalOffer(kundeId, base.id, { version: base.version, ...rest });
    }
    const status = draft.status as "ACTIVE" | "COMPLETED" | "CANCELLED";
    const rest = { ...common, orderedOn: draft.date, status, offerId: draft.offerId === "" ? null : draft.offerId };
    return base === null ? p.client.createHistoricalOrder(kundeId, { companyId: draft.companyId, ...rest })
      : p.client.updateHistoricalOrder(kundeId, base.id, { version: base.version, ...rest });
  }

  /** Sendet die Dateien, die noch kein (bzw. ein wiederholbares) Ergebnis haben; Ergebnisse stehen an ihrer Datei. */
  async function upload(id: string, list: UploadRow[], only: (r: UploadRow) => boolean): Promise<UploadRow[]> {
    const pending = list.map((r, i) => ({ r, i })).filter(({ r }) => only(r) && !isTooLarge(r.file));
    const next = list.map((r): UploadRow => (r.result === null && isTooLarge(r.file) ? { ...r, result: tooLargeResult(r.file) } : r));
    if (pending.length === 0) return next;
    setUploading(true);
    setMessage(null);
    const sent = await sendInPackets(client, kundeId, { art: refKind, id }, pending.map(({ r }) => r.file));
    setUploading(false);
    if (sent.error !== null) setMessage(apiMeldung(sent.error, DOCUMENT_FILE_TEXT.uploadFailed));
    pending.forEach(({ i }, j) => { next[i] = { file: list[i]!.file, result: sent.results[j] ?? null }; });
    return next;
  }

  /** Steht jede Datei, ist das Panel fertig; sonst bleiben die Ergebnisse je Datei stehen. */
  function finishIfSettled(id: string, result: UploadRow[]): void {
    setRows(result);
    if (result.every(settled)) p.onGespeichert(id);
  }

  /** „Erfassen“/„Speichern“: erst der Vorgang, dann die Dateien. Alles abgelegt → fertig; sonst bleibt das Panel. */
  async function submit(): Promise<void> {
    if (savedId !== null || uploading) return;
    const record = await form.aktion();
    if (record === undefined) return;
    setSavedId(record.id);
    finishIfSettled(record.id, await upload(record.id, rows, (r) => r.result === null));
  }

  async function retry(): Promise<void> {
    if (savedId === null || uploading) return;
    finishIfSettled(savedId, await upload(savedId, rows, retriable));
  }

  async function linkExisting(index: number, fileId: string, name: string): Promise<void> {
    if (savedId === null) return;
    setUploading(true);
    setMessage(null);
    try {
      await client.linkDocumentFile(kundeId, { art: refKind, id: savedId }, fileId);
      setRows((old) => old.map((r, j) => (j === index ? { ...r, result: { status: "linked", name } } : r)));
    } catch (e) {
      setMessage(apiMeldung(e, DOCUMENT_FILE_TEXT.linkFailed));
    }
    setUploading(false);
  }

  /** Ungespeichert: Eingaben im Kopf oder gewählte Dateien (vor dem Speichern) — und jeder laufende Upload. */
  const unsaved = (savedId === null && (!sameDraft(draft, initial) || rows.length > 0)) || uploading;
  /* Der Wächter gegen das Schließen des BROWSERS (wie AngebotDetail/AuftragDetail): Kreuz, Escape und Fuß fängt
     `verlassen`, einen geschlossenen Reiter nur `beforeunload`. */
  useBeforeUnload(unsaved);

  /** Der EINE Ausgang neben dem Speichern: vor dem Speichern mit Rückfrage bei Eingaben, danach „Fertig“. */
  async function verlassen(): Promise<void> {
    if (form.laeuft || uploading) return;
    if (savedId !== null) { p.onGespeichert(savedId); return; }
    if (!(await darfAbbrechen(unsaved))) return;
    p.onAbbrechen();
  }

  const fieldErrors = form.feldFehler;
  const errorProp = (field: string): { fehler?: string } =>
    (fieldErrors[field] === undefined ? {} : { fehler: fieldErrors[field] });
  const disabled = savedId !== null || uploading || form.zustand !== "bereit";
  const setField = <K extends keyof Draft>(k: K) => (value: string): void => setDraft((d) => ({ ...d, [k]: value }));
  const title = p.vorhanden === null ? T.create[art] : T.editTitle(art, p.vorhanden.displayNumber);
  const path = storage?.path ?? storagePreview(art, draft.date, draft.legacyNumber);
  const headErrors = Object.keys(fieldErrors).filter((f) => fields.includes(f)).length;
  const retryCount = rows.filter(retriable).length;
  const company = companies.find((c) => c.id === draft.companyId);

  return (
    <Modal titel={title} breite="mittel" onAbbrechen={() => { void verlassen(); }}
      abbrechenGesperrt={form.laeuft || uploading}>
      <Formular beschriftung="oben" {...form.formularProps}>
        <div className="hist__form">
          {/* SCHLECHTESTES OBEN (Hausregel): die Fehler vor dem Rest, dann die Warnung. */}
          {form.meldung !== null && <Hinweis art="fehler">{form.meldung}</Hinweis>}
          {message !== null && <Hinweis art="fehler">{message}</Hinweis>}
          {savedId !== null && !rows.every(settled) && <Hinweis art="warnung">{T.savedWithOpenFiles}</Hinweis>}

          <Vorgangsabschnitt art={art === "offer" ? "angebot" : "auftrag"} titel={SECTION_TEXT.head}
            faltung={sections.head} inhaltId="hist-kopfdaten" fehler={headErrors}>
            <div className="hist__fields">
              {p.vorhanden === null ? (
                <Auswahl id="hist-companyId" name="hist-companyId" beschriftung={T.company} wert={draft.companyId}
                  gesperrt={disabled} {...errorProp("companyId")} onGeaendert={(d) => setField("companyId")(d.wert)}>
                  {companies.length !== 1 && <option value="">{T.choose}</option>}
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>)}
                </Auswahl>
              ) : (
                <Feld id="hist-companyId" label={T.company} bearbeitbar={false} onChange={() => {}}
                  wert={company === undefined ? "" : `${company.shortCode} · ${company.name}`} />
              )}
              <Feld id="hist-title" label={T.title} bearbeitbar gesperrt={disabled} wert={draft.title}
                onChange={setField("title")} {...errorProp("title")} />
              <Feld id="hist-legacyNumber" label={T.legacyNumber} bearbeitbar gesperrt={disabled}
                wert={draft.legacyNumber} onChange={setField("legacyNumber")} {...errorProp("legacyNumber")} />
              <Feld id={`hist-${dateField}`} label={T.date} typ="date" bearbeitbar gesperrt={disabled} wert={draft.date}
                onChange={setField("date")} {...errorProp(dateField)} />
              {/* Ein Betrag ist Text mit deutschem Komma; me-eingabe `dezimal` steht rechtsbündig, die Einheit daneben. */}
              <Eingabe id="hist-netAmount" name="hist-netAmount" beschriftung={T.netAmount} art="dezimal"
                einheit={T.currencyUnit} wert={draft.netAmount} gesperrt={disabled} {...errorProp("netAmount")}
                onGeaendert={(d) => setField("netAmount")(d.wert)} />
              <Auswahl id="hist-status" name="hist-status" beschriftung={T.status} wert={draft.status}
                gesperrt={disabled} {...errorProp("status")} onGeaendert={(d) => setField("status")(d.wert)}>
                <option value="">{T.choose}</option>
                {STATUS_OPTIONS[art].map((s) => <option key={s.value} value={s.value}>{s.text}</option>)}
              </Auswahl>
              <Auswahl id="hist-contactId" name="hist-contactId" beschriftung={T.contact} wert={draft.contactId}
                gesperrt={disabled} {...errorProp("contactId")} onGeaendert={(d) => setField("contactId")(d.wert)}>
                <option value="">{T.noContact}</option>
                {choices.kontakte.map((k) => <option key={k.id} value={k.id}>{k.anzeigename}</option>)}
              </Auswahl>
              <Auswahl id="hist-responsibleUserId" name="hist-responsibleUserId" beschriftung={T.responsible}
                wert={draft.responsibleUserId} gesperrt={disabled} {...errorProp("responsibleUserId")}
                onGeaendert={(d) => setField("responsibleUserId")(d.wert)}>
                <option value="">{T.noUser}</option>
                {choices.benutzer.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Auswahl>
              {art === "order" && (
                <Auswahl id="hist-offerId" name="hist-offerId" beschriftung={T.sourceOffer} wert={draft.offerId}
                  gesperrt={disabled} {...errorProp("offerId")} onGeaendert={(d) => setField("offerId")(d.wert)}>
                  <option value="">{T.noOffer}</option>
                  {choices.historischeAngebote.map((a) => (
                    <option key={a.id} value={a.id}>{a.displayNumber} · {a.title}</option>
                  ))}
                </Auswahl>
              )}
              {art === "offer" && (
                <div className="hist__full">
                  <Feld id="hist-note" label={T.note} typ="mehrzeilig" zeilen={3} wachsend bearbeitbar
                    gesperrt={disabled} wert={draft.note} onChange={setField("note")} {...errorProp("note")} />
                </div>
              )}
            </div>
          </Vorgangsabschnitt>

          <Vorgangsabschnitt art="datei" titel={SECTION_TEXT.files} faltung={sections.files} inhaltId="hist-dateien"
            {...(rows.length === 0 ? {} : { zaehler: rows.length })}>
            <div className="hist__files">
              {savedId === null && (
                <Dateiwahl id="hist-dateiwahl" beschriftung={T.filesField} mehrere gesperrt={disabled}
                  onDateien={(f) => setRows(f.map((file) => ({ file, result: null })))} />
              )}
              <UploadResultList rows={rows} label={DOCUMENT_FILE_TEXT.results} busy={uploading}
                onLinkExisting={(index, fileId, name) => { void linkExisting(index, fileId, name); }} />
              <StorageLine path={path} willCreate={storage?.willCreate === true} pending={T.storagePending} />
              {savedId !== null && retryCount > 0 && (
                <div className="hist__actions">
                  <KiKnopf deaktiviert={uploading} onClick={() => { void retry(); }}>{T.retry}</KiKnopf>
                </div>
              )}
            </div>
          </Vorgangsabschnitt>
        </div>
      </Formular>

      {savedId === null ? (
        <ModalFuss speichernText={p.vorhanden === null ? T.save : T.saveEdit}
          speichernGesperrt={disabled} abbrechenGesperrt={form.laeuft || uploading}
          onSpeichern={() => { void submit(); }} onAbbrechen={() => { void verlassen(); }} />
      ) : (
        <ModalFuss nurSchliessen speichernText={T.done} speichernGesperrt={uploading}
          onSpeichern={() => { void verlassen(); }} onAbbrechen={() => { void verlassen(); }} />
      )}
    </Modal>
  );
}
